import React, { useState, type ReactElement } from 'react';
import { useSelector } from 'react-redux';

import { SuperAdminActions as SuperAdminDuckActions } from 'ducks/super-client';
import ActionToolbar from 'components/table/table-controls/action-toolbar';
import FilterSelectionToolbar from 'components/table/table-controls/filter-selection-toolbar';
import { Button } from 'components/styled-primitives';

import { SuperAdminActions } from 'actions';
import { useAppDispatch } from 'store/hooks';

import { selectActiveUserIds, selectFilterIsActive } from 'selectors/super-admin';
import UsersTable from './users-table';

interface SuperAdminUser {
  verified?: boolean;
  phantom?: boolean;
}

interface FilterActionButtons {
  verifyButton: ReactElement;
  unPhantomButton: ReactElement;
  clearFilterButton: ReactElement;
}

// The `buttons` prop of `ActionToolbar` consumes each entry's `.key`/`.props`,
// which a `ReactElement` supplies at runtime. Its typed contract expects a
// non-null `key`, whereas `ReactElement.key` is `string | null`, so the
// always-keyed elements are asserted to the toolbar's button element type.
type ActionToolbarButtons = React.ComponentProps<typeof ActionToolbar>['buttons'];

const Users = (): ReactElement => {
  // `selectActiveUserIds` is typed as `unknown`; the slice is a map of user id -> user.
  const selectedUsers = useSelector(selectActiveUserIds) as Record<string, SuperAdminUser>;
  const filterIsActive = useSelector(selectFilterIsActive);
  const dispatch = useAppDispatch();

  const clearFilters = (): unknown => dispatch(SuperAdminActions.clearFilters());
  const verifyUsersEmails = (users: SuperAdminUser[]): unknown =>
    dispatch(SuperAdminDuckActions.verifyUsersEmails(users));
  const unPhantomUsers = (users: SuperAdminUser[]): unknown =>
    dispatch(SuperAdminDuckActions.requestUnPhantomUsers(users));

  const [count, setCount] = useState(0);

  const filterActionButtons = (): FilterActionButtons => {
    const unverifiedSelectedUsers = Object.values(selectedUsers).filter((user) => !user.verified);
    const phantomSelectedUsers = Object.values(selectedUsers).filter(
      (user) => user.phantom === true,
    );
    return {
      verifyButton: (
        <Button
          key="verify-button"
          onClick={() => verifyUsersEmails(unverifiedSelectedUsers)}
          disabled={unverifiedSelectedUsers.length < 1}
        >
          {unverifiedSelectedUsers.length > 1
            ? `Verify ${unverifiedSelectedUsers.length} Emails`
            : `Verify Email`}
        </Button>
      ),
      unPhantomButton: (
        <Button
          key="unphantom-button"
          onClick={() => unPhantomUsers(unverifiedSelectedUsers)}
          disabled={unverifiedSelectedUsers.length < 1}
        >
          {phantomSelectedUsers.length > 1
            ? `Un-phantom ${phantomSelectedUsers.length} Accounts`
            : `Un-phantom User Account`}
        </Button>
      ),
      clearFilterButton: (
        <Button key="clear" disabled={!filterIsActive} onClick={() => clearFilters()}>
          Clear Filter
        </Button>
      ),
    };
  };

  const renderActionToolbar = (): ReactElement => {
    const { verifyButton, unPhantomButton, clearFilterButton } = filterActionButtons();

    // 'null' represents a spacer in the button layout
    return (
      <ActionToolbar
        buttons={
          [
            verifyButton,
            unPhantomButton,
            null,
            null,
            null,
            clearFilterButton,
          ] as unknown as ActionToolbarButtons
        }
      />
    );
  };

  return (
    <UsersTable
      setCount={setCount}
      filterControls={<FilterSelectionToolbar showUserFilters numberOfResults={count} />}
      controls={renderActionToolbar()}
    />
  );
};

export default Users;
